import React from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    Calculator,
    ChevronRight,
    Globe,
    Loader2,
    RefreshCw,
    Sparkles,
    Trash2,
    Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AracDiagram from "./dk-gelismis/AracDiagram";
import DkPdfSablonMenu from "./deger-kaybi-gelismis/DkPdfSablonMenu";
import DkGelismisModulGiris from "./dk-gelismis/DkGelismisModulGiris";
import HesapNetPanel from "./dk-gelismis/HesapNetPanel";
import ParcaIslemEditor from "@/components/deger-kaybi/ParcaIslemEditor";
import DkStickyBar from "./deger-kaybi-gelismis/DkStickyBar";
import DkDosyaPanel from "./deger-kaybi-gelismis/DkDosyaPanel";
import DkEkspertizRaporPanel from "./deger-kaybi-gelismis/DkEkspertizRaporPanel";
import DkSablonBar from "./deger-kaybi-gelismis/DkSablonBar";
import DkSenaryoPanel from "./deger-kaybi-gelismis/DkSenaryoPanel";
import DkIlanModal from "./deger-kaybi-gelismis/DkIlanModal";
import DkGecmisPanel from "./deger-kaybi-gelismis/DkGecmisPanel";
import DkTahkimModal from "./deger-kaybi-gelismis/DkTahkimModal";
import { PERT_HASAR_KODLARI } from "./deger-kaybi-gelismis/constants";
import { useDkGelismis } from "./deger-kaybi-gelismis/useDkGelismis";

function Step({ n, label, active, done }) {
    return (
        <div className={`flex items-center gap-2 text-xs ${active ? "text-emerald-300" : done ? "text-slate-400" : "text-slate-600"}`}>
            <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border ${
                    active
                        ? "border-emerald-400 bg-emerald-500/20 text-emerald-200"
                        : done
                          ? "border-emerald-500/40 bg-emerald-500/10"
                          : "border-white/10"
                }`}
            >
                {done ? "✓" : n}
            </span>
            {label}
        </div>
    );
}

function tl(n, fmt) {
    if (fmt) return fmt;
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return `₺${Math.round(v).toLocaleString("tr-TR")}`;
}

export default function DegerKaybiGelismisPage() {
    const dk = useDkGelismis();

    return (
        <div className="min-h-screen bg-[#060a12] pb-28">
            <div className="relative overflow-hidden border-b border-white/5">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(16,185,129,0.18),transparent)]" />
                <div className="relative p-6 max-w-[100rem] mx-auto">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-[10px] uppercase tracking-wider text-emerald-300 mb-3">
                                <Sparkles className="h-3 w-3" />
                                MBS · Eksper metodolojisi
                            </div>
                            <h1 className="font-display text-3xl md:text-4xl text-white tracking-tight">
                                Gelişmiş Değer Kaybı
                            </h1>
                            <p className="text-slate-400 mt-2 max-w-xl text-sm leading-relaxed">
                                Parça seçimi · piyasa rayiç · OCR · senaryo · PDF · WhatsApp · kalibrasyon
                                {" · "}
                                <Link to="/yeni/deger-kaybi-on-hesap" className="text-sky-400 hover:underline">
                                    Ön hesap
                                </Link>
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={dk.loading}
                            className="border-white/10"
                            onClick={() => window.location.reload()}
                        >
                            {dk.loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <RefreshCw className="h-4 w-4" />
                            )}
                        </Button>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-6">
                        <Step n={1} label="Parça seç" active={dk.adim === 1} done={dk.adim > 1} />
                        <ChevronRight className="h-4 w-4 text-slate-700 self-center hidden sm:block" />
                        <Step n={2} label="Rayiç & veri" active={dk.adim === 2} done={dk.adim > 2} />
                        <ChevronRight className="h-4 w-4 text-slate-700 self-center hidden sm:block" />
                        <Step n={3} label="Net hesap" active={dk.adim === 3} done={dk.adim > 3} />
                        <ChevronRight className="h-4 w-4 text-slate-700 self-center hidden sm:block" />
                        <Step n={4} label="Kaydet / paylaş" active={dk.adim === 4} done={false} />
                    </div>
                </div>
            </div>

            <div className="p-6 max-w-[100rem] mx-auto space-y-6">
                <DkGelismisModulGiris />
                <DkEkspertizRaporPanel
                    loading={dk.loading}
                    sonuc={dk.ekspertizSonuc}
                    seciliDosya={dk.seciliDosya}
                    onAnaliz={dk.ekspertizRaporAnaliz}
                    onTemizle={dk.ekspertizTemizle}
                    onFaturaOcr={dk.faturaOcrAc}
                    onPdf={(dk.hesap || dk.ekspertizSonuc?.hesap) ? (sablon) => dk.pdfIndir(sablon) : undefined}
                    pdfLoading={dk.pdfLoading}
                />

                <DkPdfSablonMenu
                    disabled={dk.loading || !(dk.hesap || dk.ekspertizSonuc?.hesap)}
                    loading={dk.pdfLoading}
                    onIndir={
                        dk.hesap || dk.ekspertizSonuc?.hesap ? (s) => dk.pdfIndir(s) : undefined
                    }
                    hint={
                        dk.hesap || dk.ekspertizSonuc?.hesap
                            ? undefined
                            : "PDF indirmek için önce Hesapla veya ekspertiz raporu analiz edin."
                    }
                />

                <div className="rounded-xl border border-[#D4AF37]/20 bg-[#0c1222]/80 p-4 md:p-5 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <h3 className="text-sm font-semibold text-white">Hasarlı parça seçimi</h3>
                            <p className="text-[11px] text-slate-500">
                                <span className="text-emerald-400">{dk.seciliParcalar.length} seçili</span>
                                {dk.onizleme?.tutar ? (
                                    <span className="text-violet-300 ml-2">Önizleme: {dk.onizleme.tutar}</span>
                                ) : null}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={dk.temizleParcalar}
                            className="text-[11px] text-slate-500 hover:text-rose-400 flex items-center gap-1 px-2 py-1 rounded border border-white/10"
                        >
                            <Trash2 className="h-3 w-3" />
                            Temizle
                        </button>
                    </div>
                    <DkSablonBar sablonlar={dk.sablonlar} disabled={dk.loading} onApply={dk.uygulaSablon} />
                    <div className="grid md:grid-cols-[minmax(0,22rem)_1fr] gap-4 items-start">
                        <AracDiagram
                            zones={dk.zones}
                            secili={dk.seciliParcalar}
                            onToggle={dk.parcaToggle}
                            onHover={dk.setHoverParca}
                            disabled={dk.loading}
                            diagramMeta={dk.diagram}
                            gorunumler={dk.gorunumler}
                            aktifGorunum={dk.diagramGorunum}
                            onGorunumChange={dk.setDiagramGorunum}
                        />
                        <div className="flex flex-wrap gap-1.5 content-start max-h-[220px] overflow-y-auto pr-1">
                            {dk.zones.map((z) => {
                                const sec = dk.seciliParcalar.some((p) => p.diagram_id === z.id);
                                return (
                                    <button
                                        key={z.id}
                                        type="button"
                                        disabled={dk.loading}
                                        onClick={() =>
                                            dk.parcaToggle({
                                                id: z.id,
                                                etiket: z.etiket,
                                                varsayilan_islem: z.varsayilan_islem,
                                            })
                                        }
                                        className={`px-2 py-1 rounded-md text-[10px] border transition-colors ${
                                            sec
                                                ? "bg-[#D4AF37]/20 border-[#D4AF37]/50 text-[#F3E5AB]"
                                                : "border-white/10 text-slate-500 hover:border-emerald-500/40 hover:text-slate-300"
                                        }`}
                                    >
                                        {z.etiket}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-4 space-y-4">
                        <DkDosyaPanel
                            dosyaQ={dk.dosyaQ}
                            setDosyaQ={dk.setDosyaQ}
                            dosyalar={dk.dosyalar}
                            seciliDosya={dk.seciliDosya}
                            onDosyaSec={dk.dosyaSec}
                            onEkspertizDoldur={dk.ekspertizdenDoldur}
                            loading={dk.loading}
                        />
                        <DkGecmisPanel gecmisHesaplar={dk.gecmisHesaplar} plaka={dk.seciliDosya?.plaka} />

                        <div className="rounded-2xl border border-white/10 bg-[#0c1222]/90 backdrop-blur p-4 space-y-3">
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Araç & hasar
                            </h3>
                            {PERT_HASAR_KODLARI.has(dk.hasarDurumu) ? (
                                <div className="flex gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-lg p-2">
                                    <AlertTriangle className="h-4 w-4 shrink-0" />
                                    Pert / ağır hasar — üst sınırlar ve rapor dili farklı değerlendirilir.
                                </div>
                            ) : null}
                            <div className="grid grid-cols-2 gap-2">
                                <Input placeholder="Marka *" value={dk.marka} onChange={(e) => dk.setMarka(e.target.value)} className="bg-black/40 border-white/10 h-10" />
                                <Input placeholder="Model" value={dk.model} onChange={(e) => dk.setModel(e.target.value)} className="bg-black/40 border-white/10 h-10" />
                                <Input placeholder="Yıl" type="number" value={dk.modelYili} onChange={(e) => dk.setModelYili(e.target.value)} className="bg-black/40 border-white/10 h-10" />
                                <Input placeholder="KM" type="number" value={dk.km} onChange={(e) => dk.setKm(e.target.value)} onBlur={dk.kmOner} className="bg-black/40 border-white/10 h-10" />
                                <select value={dk.vites} onChange={(e) => dk.setVites(e.target.value)} className="h-10 rounded-lg bg-black/40 border border-white/10 text-sm text-white px-2">
                                    {(dk.diagram?.vites_secenekleri || []).map((o) => (
                                        <option key={o.kod} value={o.kod}>Vites: {o.etiket}</option>
                                    ))}
                                </select>
                                <select value={dk.yakit} onChange={(e) => dk.setYakit(e.target.value)} className="h-10 rounded-lg bg-black/40 border border-white/10 text-sm text-white px-2">
                                    {(dk.diagram?.yakit_secenekleri || []).map((o) => (
                                        <option key={o.kod} value={o.kod}>Yakıt: {o.etiket}</option>
                                    ))}
                                </select>
                                <Input placeholder="KM min" type="number" value={dk.kmMin} onChange={(e) => dk.setKmMin(e.target.value)} className="bg-black/40 border-white/10 h-10" />
                                <Input placeholder="KM max" type="number" value={dk.kmMax} onChange={(e) => dk.setKmMax(e.target.value)} className="bg-black/40 border-white/10 h-10" />
                                <Input placeholder="Kusur %" type="number" value={dk.kusur} onChange={(e) => dk.setKusur(e.target.value)} className="bg-black/40 border-white/10 h-10" />
                                <select value={dk.hasarDurumu} onChange={(e) => dk.setHasarDurumu(e.target.value)} className="h-10 rounded-lg bg-black/40 border border-white/10 text-sm text-white px-2">
                                    {(dk.diagram?.hasar_durumu_secenekleri || []).map((o) => (
                                        <option key={o.kod} value={o.kod}>{o.etiket}</option>
                                    ))}
                                </select>
                            </div>
                            <Input placeholder="Tramer ₺" type="number" value={dk.tramer} onChange={(e) => dk.setTramer(e.target.value)} className="bg-black/40 border-white/10 h-10" />
                            <Input placeholder="Hasar faturası ₺" type="number" value={dk.hasarFatura} onChange={(e) => dk.setHasarFatura(e.target.value)} className="bg-black/40 border-white/10 h-10" />

                            <div className="space-y-2 pt-1 border-t border-white/5">
                                <label className="text-[10px] uppercase tracking-wider text-slate-500">Hesap yöntemi</label>
                                <select
                                    value={dk.hesapYontemi}
                                    onChange={(e) => dk.setHesapYontemi(e.target.value)}
                                    className="w-full h-10 rounded-lg bg-black/40 border border-white/10 text-sm text-white px-2"
                                >
                                    <option value="karsilastirma">Karşılaştırma (3 yöntem — önerilen)</option>
                                    <option value="resmi">Resmi tablo (sigorta A1-A4)</option>
                                    <option value="mbs_hibrit">MBS Hibrit (iç metod)</option>
                                    <option value="serbest_piyasa">Serbest piyasa (Yargıtay)</option>
                                </select>
                                <select
                                    value={dk.aracNiteligi}
                                    onChange={(e) => dk.setAracNiteligi(e.target.value)}
                                    className="w-full h-10 rounded-lg bg-black/40 border border-white/10 text-sm text-white px-2"
                                >
                                    <option value="hususi">Hususi araç</option>
                                    <option value="ticari">Ticari / taksi / kiralık</option>
                                </select>
                            </div>

                            <div className="flex gap-2">
                                <Input placeholder="Rayiç (hasarsız) ₺" type="number" value={dk.rayic} onChange={(e) => dk.setRayic(e.target.value)} className="bg-black/40 border-white/10 h-10 flex-1 font-medium text-emerald-200" />
                                <Button type="button" variant="outline" disabled={dk.loading} onClick={dk.rayicAra} className="border-violet-500/30" title="Hasarsız piyasa rayiç">
                                    <Globe className="h-4 w-4 text-violet-300" />
                                </Button>
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Rayiç onarım sonrası (hasarlı) ₺"
                                    type="number"
                                    value={dk.rayicHasarli}
                                    onChange={(e) => dk.setRayicHasarli(e.target.value)}
                                    className="bg-black/40 border-white/10 h-10 flex-1 text-sky-200"
                                />
                                <Button type="button" variant="outline" disabled={dk.loading} onClick={dk.rayicHasarliAra} className="border-sky-500/30" title="Hasarlı piyasa rayiç">
                                    <Globe className="h-4 w-4 text-sky-300" />
                                </Button>
                            </div>
                            {dk.rayicSonuc?.guven != null ? (
                                <div className="text-[11px] text-violet-300/90">
                                    Güven: {dk.rayicSonuc.guven}/100 · {dk.rayicSonuc.ilan_adet || 0} ilan
                                    {(dk.rayicSonuc.ilan_adet || 0) < 5 ? (
                                        <span className="text-amber-400"> · Az ilan — rayiçi doğrulayın</span>
                                    ) : null}
                                </div>
                            ) : null}
                            {dk.rayicHasarliSonuc?.medyan_fmt ? (
                                <p className="text-[11px] text-sky-300/80">Hasarlı medyan {dk.rayicHasarliSonuc.medyan_fmt}</p>
                            ) : null}
                        </div>

                        <div className="space-y-2 hidden lg:block">
                            <Button className="w-full h-11 bg-gradient-to-r from-emerald-600 to-teal-600" disabled={dk.loading} onClick={() => dk.hesapla(false)}>
                                <Calculator className="h-4 w-4 mr-2" />
                                Net hesapla
                            </Button>
                            <Button variant="outline" className="w-full border-[#D4AF37]/30 text-[#D4AF37]" disabled={dk.loading} onClick={() => dk.hesapla(true)}>
                                <Zap className="h-4 w-4 mr-2" />
                                Piyasa rayiç + hesap
                            </Button>
                        </div>
                    </div>

                    <div className="lg:col-span-8 space-y-4">
                        <div className="rounded-2xl border border-white/10 bg-[#0c1222]/90 max-h-[420px] overflow-hidden flex flex-col">
                            <div className="px-4 py-3 border-b border-white/10 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Seçili parçalar
                            </div>
                            <div className="overflow-y-auto flex-1 divide-y divide-white/5">
                                {dk.seciliParcalar.length === 0 ? (
                                    <p className="p-6 text-sm text-slate-500 text-center">Diyagramdan parça seçin</p>
                                ) : (
                                    dk.seciliParcalar.map((p) => (
                                        <div key={p.diagram_id} className={`p-3 space-y-2 ${dk.hoverParca?.id === p.diagram_id ? "bg-white/[0.04]" : ""}`}>
                                            <div className="text-sm text-white font-medium">{p.parca_adi}</div>
                                            <ParcaIslemEditor compact value={p} onChange={(next) => dk.parcaIslemChange(p.diagram_id, next)} />
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {!dk.hesap ? (
                            <div className="rounded-2xl border border-dashed border-emerald-500/20 p-8 text-center">
                                <Calculator className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                                <p className="text-sm text-slate-500">Parça + rayiç hazır olunca net hesap burada açılır.</p>
                            </div>
                        ) : null}
                    </div>
                </div>

                {dk.hesap ? (
                    <div className="rounded-2xl border border-emerald-500/20 bg-[#0a1018] p-6 space-y-4">
                        <HesapNetPanel hesap={dk.hesap} />
                        {dk.hesap.ogrenme_carpani && Math.abs(dk.hesap.ogrenme_carpani - 1) > 0.001 ? (
                            <p className="text-xs text-violet-300">
                                Kalibrasyon çarpanı: <strong>{Number(dk.hesap.ogrenme_carpani).toFixed(3)}</strong>
                                {dk.hesap.gercek_kalibre_oncesi_fmt ? (
                                    <span className="text-slate-500"> · öncesi {dk.hesap.gercek_kalibre_oncesi_fmt}</span>
                                ) : null}
                                {" · "}
                                <Link to="/yeni/dk-kalibrasyon" className="text-[#D4AF37] hover:underline">
                                    Kalibrasyon merkezi
                                </Link>
                            </p>
                        ) : null}
                    </div>
                ) : null}

                <DkSenaryoPanel senaryo={dk.senaryo} loading={dk.loading} onHesapla={dk.senaryoHesapla} />

                {dk.rayicSonuc?.ornek_ilanlar?.length ? (
                    <div className="rounded-xl border border-violet-500/15 p-4">
                        <h4 className="text-xs text-violet-300 mb-3 uppercase tracking-wider">Piyasa örnek ilanları</h4>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {dk.rayicSonuc.ornek_ilanlar.slice(0, 6).map((il, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => dk.setSeciliIlan(il)}
                                    className="text-left text-[11px] p-2 rounded-lg bg-black/30 border border-white/5 text-slate-400 hover:border-violet-500/30"
                                >
                                    <div className="text-emerald-400 font-medium">{tl(il.fiyat)}</div>
                                    <div className="truncate mt-0.5">{il.baslik || "İlan"}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>

            <DkStickyBar
                loading={dk.loading}
                pdfLoading={dk.pdfLoading}
                hesap={dk.hesap || dk.ekspertizSonuc?.hesap}
                onizleme={dk.onizleme}
                seciliParcalar={dk.seciliParcalar}
                rayic={dk.rayic}
                kusur={dk.kusur}
                seciliDosya={dk.seciliDosya}
                onHesapla={dk.hesapla}
                onPiyasaHesap={dk.hesapla}
                onKaydet={dk.kaydet}
                onPdf={dk.hesap || dk.ekspertizSonuc?.hesap ? (s) => dk.pdfIndir(s) : null}
                onWhatsapp={dk.whatsappGonder}
                onTahkimPaket={dk.tahkimPaketOlustur}
            />

            <DkIlanModal ilan={dk.seciliIlan} onClose={() => dk.setSeciliIlan(null)} />
            <DkTahkimModal
                paket={dk.tahkimPaket}
                onClose={() => dk.setTahkimPaket(null)}
                onPdf={dk.hesap || dk.ekspertizSonuc?.hesap ? () => dk.pdfIndir({ sablon: "bilirkisi_uzman", tahkim_paket: true }) : null}
            />
        </div>
    );
}
